import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Mic, MicOff, RotateCcw, Volume2, Star, CheckCircle2 } from 'lucide-react';
import { useTranslation } from '../../../i18n';

const DEFAULT_THRESHOLDS = [0.30, 0.50, 0.70, 0.85, 0.95];

const normalize = (s) => (s || '')
  .toString()
  .trim()
  .toLowerCase()
  .replace(/[.,!?;:"'`()[\]{}]/g, '')
  .replace(/\s+/g, ' ');

// Levenshtein-based similarity in [0..1]. 1 = exact match.
const similarity = (a, b) => {
  const A = normalize(a);
  const B = normalize(b);
  if (!A && !B) return 1;
  if (!A || !B) return 0;
  if (A === B) return 1;
  const m = A.length;
  const n = B.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = A[i - 1] === B[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost,
      );
    }
  }
  const dist = dp[m][n];
  return 1 - dist / Math.max(m, n);
};

// Returned score combines string similarity with recognizer confidence when
// the browser provides it. Web Speech confidence is unreliable across
// browsers (Safari often returns 0), so similarity is the primary signal.
export const computePronounceScore = (transcript, target, confidence) => {
  const sim = similarity(transcript, target);
  if (sim < 0.99) return sim;
  if (typeof confidence === 'number' && confidence > 0) {
    return Math.min(1, 0.85 + confidence * 0.15);
  }
  return sim;
};

export const scoreToStars = (score, thresholds) => {
  const t = Array.isArray(thresholds) && thresholds.length === 5
    ? thresholds
    : DEFAULT_THRESHOLDS;
  let stars = 0;
  for (let i = 5; i >= 1; i--) {
    if (score >= t[i - 1]) { stars = i; break; }
  }
  return stars;
};

const speechRecognitionSupported = () => (
  typeof window !== 'undefined'
  && !!(window.SpeechRecognition || window.webkitSpeechRecognition)
);

const PronounceElement = ({
  element,
  isEditor = false,
  studentView = false,
  currentState = null,
  showResult = false,
  isCorrect = null,
  onStateChange,
  onCheck,
  onRetry,
  disabled = false,
  hideCheckButton = false,
}) => {
  const { t } = useTranslation();
  const { content, style } = element;
  const {
    targetText = '',
    language = 'en-US',
    starThresholds,
    allowRetry = false,
    buttonColor = '#2A4D25',
    actionBarColor = '#f9fafb',
    fontFamily = 'Inter',
    fontSize = 24,
    textColor = '#1f2937',
    showTargetToStudent = true,
  } = content || {};

  const backgroundColor = style?.backgroundColor || '#ffffff';
  const isActionBarTransparent = actionBarColor === 'transparent';

  const recognitionRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const transcript = currentState?.transcript || '';
  const confidence = currentState?.confidence ?? null;
  const supported = useMemo(() => speechRecognitionSupported(), []);

  const emit = (next) => onStateChange?.(next);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch (_) {}
        recognitionRef.current = null;
      }
    };
  }, []);

  const startListening = () => {
    if (disabled || isEditor || showResult) return;
    if (!supported) {
      setErrorMsg(t('games.pronounce.errBrowser'));
      return;
    }
    setErrorMsg('');
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognition.lang = language || 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event) => {
      const res = event.results?.[0]?.[0];
      const text = (res?.transcript || '').trim();
      const conf = typeof res?.confidence === 'number' ? res.confidence : null;
      emit({ transcript: text, confidence: conf });
      // Auto-check on result — matches the flow the user expects from a
      // "record → grade" interaction; no second click needed.
      onCheck?.({ transcript: text, confidence: conf });
    };
    recognition.onerror = (event) => {
      const err = event.error;
      if (err === 'not-allowed' || err === 'service-not-allowed') {
        setErrorMsg(t('games.pronounce.errMicDenied'));
      } else if (err === 'no-speech') {
        setErrorMsg(t('games.pronounce.errNoSpeech'));
      } else if (err === 'audio-capture') {
        setErrorMsg(t('games.pronounce.errNoMic'));
      } else {
        setErrorMsg(t('games.pronounce.errRecognition'));
      }
      setIsListening(false);
    };
    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch (e) {
      setErrorMsg(t('games.pronounce.errStart'));
      setIsListening(false);
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_) {}
    }
  };

  const playTarget = () => {
    if (!targetText || typeof window === 'undefined' || !window.speechSynthesis) return;
    try {
      const utter = new SpeechSynthesisUtterance(targetText);
      utter.lang = language || 'en-US';
      const voices = window.speechSynthesis.getVoices();
      const match = voices.find((v) => v.lang === utter.lang) || voices.find((v) => v.lang?.startsWith(utter.lang.split('-')[0]));
      if (match) utter.voice = match;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utter);
    } catch (_) {}
  };

  const handleRetry = () => {
    setErrorMsg('');
    stopListening();
    onRetry?.();
  };

  const score = useMemo(
    () => (showResult ? computePronounceScore(transcript, targetText, confidence) : 0),
    [showResult, transcript, targetText, confidence],
  );
  const earnedStars = useMemo(
    () => (showResult ? scoreToStars(score, starThresholds) : 0),
    [showResult, score, starThresholds],
  );

  const getBorderColor = () => {
    if (!showResult) return 'border-transparent';
    return isCorrect ? 'border-green-400' : 'border-red-400';
  };
  const getBgStyle = () => {
    if (!showResult) return { backgroundColor };
    return { backgroundColor: isCorrect ? '#f0fdf4' : '#fef2f2' };
  };

  const hideChrome = true;

  // ---- EDITOR PREVIEW ----
  if (isEditor) {
    return (
      <div
        className="w-full h-full flex flex-col rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm"
        style={{ backgroundColor }}
      >
        <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center gap-2 text-xs text-gray-600 overflow-hidden">
          <Mic size={12} className="text-emerald-500 flex-shrink-0" />
          <span className="font-medium text-gray-700 flex-shrink-0">{t('games.pronounce.label')}</span>
          <span className="text-gray-500 truncate">· {language}</span>
        </div>
        <div className="flex-1 min-h-0 p-3 flex flex-col items-center justify-center gap-3 overflow-hidden">
          <div
            className="font-bold text-center break-words w-full"
            style={{
              fontFamily,
              fontSize: `${fontSize}px`,
              color: textColor,
            }}
          >
            {targetText || t('games.pronounce.enterWord')}
          </div>
          <div className="flex items-center gap-2 opacity-60">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: buttonColor }}
            >
              <Mic size={20} className="text-white" />
            </div>
            <button
              disabled
              type="button"
              className="p-2 rounded-full bg-white border border-gray-200 text-gray-400 cursor-not-allowed"
              title={t('games.pronounce.listen')}
            >
              <Volume2 size={16} />
            </button>
          </div>
        </div>
        <div
          className={`px-3 py-2 flex items-center justify-end ${
            isActionBarTransparent ? '' : 'border-t border-gray-100'
          }`}
          style={{ backgroundColor: actionBarColor }}
        >
          <div className="flex items-center gap-1 mr-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} size={14} className="text-gray-300 fill-gray-200" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ---- INTERACTIVE ----
  return (
    <div
      className={`w-full h-full flex flex-col rounded-xl overflow-hidden ${
        hideChrome ? '' : `border-2 ${getBorderColor()} shadow-sm`
      }`}
      style={getBgStyle()}
    >
      {/* Target word / listen button */}
      <div className="flex-1 min-h-0 p-4 flex flex-col items-center justify-center gap-4 overflow-hidden">
        {showTargetToStudent && targetText ? (
          <div className="flex items-center gap-3 max-w-full">
            <div
              className="font-bold text-center break-words"
              style={{
                fontFamily,
                fontSize: `${fontSize}px`,
                color: textColor,
              }}
            >
              {targetText}
            </div>
            <button
              type="button"
              onClick={playTarget}
              className="p-2 rounded-full bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 flex-shrink-0 transition"
              title={t('games.pronounce.listen')}
            >
              <Volume2 size={18} />
            </button>
          </div>
        ) : !targetText ? (
          <p className="text-sm text-gray-400">{t('games.pronounce.wordNotSet')}</p>
        ) : null}

        {/* Mic button */}
        {!showResult && (
          <button
            type="button"
            onClick={isListening ? stopListening : startListening}
            disabled={disabled || !targetText || !supported}
            className={`w-20 h-20 rounded-full flex items-center justify-center shadow-lg transition-all ${
              disabled || !targetText || !supported
                ? 'bg-gray-300 cursor-not-allowed'
                : isListening
                  ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                  : 'hover:scale-105'
            }`}
            style={{
              backgroundColor: (disabled || !targetText || !supported)
                ? undefined
                : (isListening ? undefined : buttonColor),
            }}
            title={isListening ? t('games.pronounce.stop') : t('games.pronounce.speakWord')}
          >
            {isListening ? (
              <MicOff size={32} className="text-white" />
            ) : (
              <Mic size={32} className="text-white" />
            )}
          </button>
        )}

        {!showResult && !isListening && (
          <p className="text-xs text-gray-500 text-center">
            {t('games.pronounce.tapMicAndSpeak')}
          </p>
        )}

        {!showResult && isListening && (
          <p className="text-xs text-red-600 font-medium text-center">
            {t('games.pronounce.listening')}
          </p>
        )}

        {errorMsg && (
          <p className="text-xs text-red-600 text-center px-2">{errorMsg}</p>
        )}

        {showResult && transcript && (
          <div className="w-full max-w-md flex flex-col items-center gap-2">
            <span className="text-xs text-gray-500 uppercase tracking-wide">{t('games.pronounce.heard')}</span>
            <span className="text-lg font-medium text-gray-800 text-center break-words">
              «{transcript}»
            </span>
          </div>
        )}
      </div>

      {/* Action bar */}
      <div
        className={`px-3 py-2 flex items-center justify-end gap-2 ${
          hideChrome || isActionBarTransparent ? '' : 'border-t border-gray-100'
        }`}
        style={{ backgroundColor: hideChrome ? 'transparent' : actionBarColor }}
      >
        {showResult && allowRetry && (
          <button
            onClick={handleRetry}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
          >
            <RotateCcw size={14} />
            {t('games.common.tryAgain')}
          </button>
        )}

        {showResult && (
          <div className="flex items-center gap-1 mr-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star
                key={i}
                size={16}
                className={i < earnedStars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 fill-gray-200'}
              />
            ))}
            <span className="ml-1 text-xs font-semibold text-gray-700">{earnedStars}/5</span>
          </div>
        )}

        {showResult && !hideCheckButton && (
          <div
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${
              isCorrect ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
            }`}
          >
            {isCorrect ? (
              <>
                <CheckCircle2 size={16} />
                {t('games.pronounce.good')}
              </>
            ) : (
              <>
                <span className="text-lg leading-none">≈</span>
                {t('games.pronounce.tryAgainShort')}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PronounceElement;
